/**
 * The site footer, and the credits it opens.
 *
 * This is not decoration. Every photograph in the museum is Creative Commons, and those
 * licences require the credit and the licence to travel with the work. A CREDITS.md in
 * the repository does not reach a visitor, so until now the obligation was met for the
 * source tree and not for the thing actually published.
 *
 * The panel also declares the reconstructions. An interpretation shown beside a
 * photograph has to say which one it is, in one findable place as well as on the image.
 */

import { creditsFor } from "./collection";
import credits from "./credits.json";
import { BRAND, type UiCopy } from "./i18n";
import qattBand from "./ornament/qatt-divider.webp?url";
import { DEFAULT_REGION, findRegion, type RegionId } from "./regions";

type CreditedWork = { exhibition: string; title: string; photographer: string; licence: string; licence_url: string };

/**
 * The licensed works belonging to one heritage.
 *
 * `credits.json` is the manifest for the Saudi exhibitions, and it used to be rendered
 * whole under whichever heritage was on screen — so the Greek page credited forty
 * photographs of Hegra it does not contain, under a sentence claiming every photograph
 * on the page came from Wikimedia Commons. Neither was true there, and an attribution
 * panel that lists the wrong works is worse than none: it is a false statement about
 * who made what.
 */
function worksFor(region: RegionId): CreditedWork[] {
  const slugs = findRegion(region).slugs;
  return (credits.works as CreditedWork[]).filter((work) => slugs.includes(work.exhibition));
}

export function createFooter(copy: UiCopy, region: RegionId = DEFAULT_REGION): HTMLElement {
  const footer = document.createElement("footer");
  footer.className = "site-footer";

  const band = document.createElement("div");
  band.className = "site-footer__band";
  band.style.backgroundImage = `url(${qattBand})`;
  band.setAttribute("aria-hidden", "true");

  const inner = document.createElement("div");
  inner.className = "site-footer__inner";

  const brand = document.createElement("div");
  brand.className = "site-footer__brand";
  const name = document.createElement("p");
  name.className = "site-footer__name";
  name.textContent = BRAND;
  const tagline = document.createElement("p");
  tagline.className = "site-footer__tagline";
  tagline.textContent = copy.footerTagline;
  brand.append(name, tagline);

  const actions = document.createElement("div");
  actions.className = "site-footer__actions";

  const dialog = createCreditsDialog(copy, region);
  const openCredits = document.createElement("button");
  openCredits.type = "button";
  openCredits.className = "site-footer__link";
  openCredits.textContent = copy.footerCredits;
  openCredits.addEventListener("click", () => dialog.showModal());

  const attribution = document.createElement("p");
  attribution.className = "site-footer__note";
  // The note under the button describes where this page's pictures come from, so it
  // follows the same split as the panel itself.
  attribution.textContent =
    worksFor(region).length > 0 ? copy.footerAttribution : copy.footerAttributionCollection;

  actions.append(openCredits, attribution);
  inner.append(brand, actions);
  footer.append(band, inner, dialog);
  return footer;
}

function createCreditsDialog(copy: UiCopy, region: RegionId): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "credits panel";
  dialog.setAttribute("aria-labelledby", "credits-title");

  const heading = document.createElement("h2");
  heading.id = "credits-title";
  heading.textContent = copy.creditsTitle;

  const works = worksFor(region);
  const contributors = creditsFor(region);

  const intro = document.createElement("p");
  intro.textContent = copy.creditsPhotography;

  const close = document.createElement("button");
  close.type = "button";
  close.className = "credits__close";
  close.textContent = copy.closeHelp;
  close.setAttribute("aria-label", copy.creditsClose);
  close.addEventListener("click", () => dialog.close());

  const list = document.createElement("ul");
  list.className = "credits__list";
  for (const work of works) {
    const item = document.createElement("li");

    const title = document.createElement("span");
    title.className = "credits__work";
    title.textContent = work.title;

    const by = document.createElement("span");
    by.className = "credits__by";
    by.textContent = work.photographer;

    // The licence is a link because the licence text is the thing being pointed at, and
    // naming it without letting anyone read it is half an attribution.
    const licence = document.createElement("a");
    licence.className = "credits__licence";
    licence.href = work.licence_url;
    licence.textContent = work.licence;
    licence.rel = "noopener noreferrer license";
    licence.target = "_blank";

    item.append(title, by, licence);
    list.append(item);
  }

  const reconstructions = document.createElement("p");
  reconstructions.className = "credits__note";
  reconstructions.textContent = copy.creditsReconstructions;

  const typefaces = document.createElement("p");
  typefaces.className = "credits__note";
  typefaces.textContent = `${copy.creditsTypefaces} ${credits.typefaces
    .map((face) => face.name)
    .join(" · ")}`;

  dialog.append(heading);
  // Both halves are conditional because both are claims. The Wikimedia sentence and the
  // reconstruction note describe the exhibitions; the contributor block describes work
  // lent to us by name. A heritage has one, the other, or in principle both — and
  // printing a paragraph about material that is not on the page is the leftover this
  // whole split exists to remove.
  if (works.length > 0) dialog.append(intro, list, reconstructions);
  if (contributors.length > 0) dialog.append(createContributors(copy, contributors));
  dialog.append(typefaces, close);
  return dialog;
}

/**
 * The people who lent this heritage their own work.
 *
 * Not a licence list: there is no Creative Commons deed to point at, so what is owed is
 * the name and a way to reach them. Kept out of `.credits__list`, which is per-image and
 * whose length is asserted against the manifest.
 */
function createContributors(
  copy: UiCopy,
  contributors: readonly { name: string; url: string }[],
): DocumentFragment {
  const fragment = document.createDocumentFragment();

  const lead = document.createElement("p");
  lead.textContent = copy.creditsCollection;

  const list = document.createElement("ul");
  list.className = "credits__contributors";
  for (const contributor of contributors) {
    const item = document.createElement("li");
    const link = document.createElement("a");
    link.href = contributor.url;
    link.textContent = contributor.name;
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    item.append(link);
    list.append(item);
  }

  fragment.append(lead, list);
  return fragment;
}
